<script lang="ts">
  import Select from "@/components/Select.svelte";
  import AdminShell from "@/components/AdminShell.svelte";
  import { api, ApiError, type AdminServer } from "@/api";

  import { token, user } from "@/stores/session";
  import { confirmModal } from "@/stores/modal";
  import { routeHref } from "@/router";
  import { statusLabels, transitionalStatuses } from "@/config";
  import Card from "@/components/Card.svelte";
  import Badge from "@/components/Badge.svelte";
  import Button from "@/components/Button.svelte";
  import Alert from "@/components/Alert.svelte";
  import AdminTable from "@/components/AdminTable.svelte";
  import EmptyState from "@/components/EmptyState.svelte";
  import LoadingState from "@/components/LoadingState.svelte";
  import TextInput from "@/components/TextInput.svelte";
  import { formatDateTime } from "@/utils/format";
  import { startTransitionalPoll } from "@/utils/transitional-poll";

  let nextCursor = $state<string | null>(null);
  let pageCursors = $state<Array<string | null>>([null]);
  let servers: AdminServer[] = $state([]);

  let query = $state("");
  let debouncedQuery = $state("");
  let status = $state("");
  let page = $state(1);
  let loading = $state(true);
  let busyId = $state<string | null>(null);
  let error = $state<string | null>(null);

  const isAdmin = $derived($user?.role === "admin");
  const visible = $derived(servers);

  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  let serversAbort: AbortController | null = null;

  $effect(() => {
    const term = query;
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    searchTimer = setTimeout(() => {
      debouncedQuery = term;
    }, 300);
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
  });

  async function load(targetPage = 1) {
    if (!$token) {
      return;
    }
    serversAbort?.abort();
    if (targetPage === 1) {
      nextCursor = null;
      pageCursors = [null];
      page = 1;
      servers = [];
    }
    const controller = new AbortController();
    serversAbort = controller;
    loading = true;
    try {
      const [result] = await Promise.all([
        api.adminServers(
          $token,
          debouncedQuery,
          status,
          controller.signal,
          targetPage === 1 ? null : targetPage > page ? nextCursor : pageCursors[targetPage - 1],
        ),
      ]);
      if (controller.signal.aborted) {
        return;
      }
      const cursor =
        targetPage === 1 ? null : targetPage > page ? nextCursor : pageCursors[targetPage - 1];
      pageCursors = targetPage === 1 ? [null] : [...pageCursors.slice(0, targetPage - 1), cursor];
      servers = result.items;
      nextCursor = result.nextCursor;
      page = targetPage;
      error = null;
    } catch (cause) {
      if ((cause as Error).name === "AbortError" || controller.signal.aborted) {
        return;
      }
      error = cause instanceof ApiError ? cause.message : "Не удалось загрузить серверы";
    } finally {
      if (!controller.signal.aborted) {
        loading = false;
      }
    }
  }

  function handleSearch(event: SubmitEvent) {
    event.preventDefault();
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    if (debouncedQuery !== query) {
      debouncedQuery = query;
    } else {
      void load();
    }
  }

  async function action(server: AdminServer, type: "start" | "stop" | "kill" | "delete") {
    if (!$token || busyId || (type === "delete" && !isAdmin)) {
      return;
    }
    const label =
      type === "delete"
        ? "Удалить сервер? Операция необратима."
        : type === "start"
          ? "Запустить сервер?"
          : type === "kill"
            ? "Аварийно завершить процесс контейнера?"
            : "Поставить сервер на остановку?";
    const confirmed = await confirmModal(
      label,
      `${server.name} · владелец ${server.ownerUsername}`,
      type === "delete"
        ? "Удалить"
        : type === "start"
          ? "Запустить"
          : type === "kill"
            ? "Аварийно остановить процесс"
            : "Остановить",
    );
    if (!confirmed) {
      return;
    }
    busyId = server.id;
    try {
      if (type === "start") {
        await api.adminStartServer($token, server.id);
      } else if (type === "stop") {
        await api.adminStopServer($token, server.id);
      } else if (type === "kill") {
        await api.adminKillServer($token, server.id);
      } else {
        await api.adminDeleteServer($token, server.id);
      }
      await load();
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Операция не выполнена";
    } finally {
      busyId = null;
    }
  }

  $effect(() => {
    if (!servers.some((server) => transitionalStatuses.has(server.status))) {
      return;
    }
    const poll = startTransitionalPoll({
      request: async () => {
        await load();
        return true;
      },
      intervalMs: 4000,
    });
    return () => poll.stop();
  });

  $effect(() => {
    void debouncedQuery;
    void status;
    void load();
    return () => {
      serversAbort?.abort();
    };
  });
</script>

<AdminShell section="servers">
  {#if error}
    <Alert type="error"
      >{error}
      <Button variant="ghost" size="compact" disabled={loading} onclick={() => void load()}
        >Повторить загрузку</Button
      ></Alert
    >
  {/if}

  <Card class="admin-card admin-wide-card">
    <div class="admin-toolbar">
      <div>
        <p class="eyebrow">Серверы пользователей</p>
        <h2>Серверы <small>{servers.length}</small></h2>
      </div>

      <form class="admin-search admin-server-search" onsubmit={handleSearch}>
        <TextInput
          density="compact"
          flexible
          bind:value={query}
          placeholder="Имя или владелец"
          aria-label="Поиск серверов"
        />
        <Select
          bind:value={status}
          aria-label="Статус"
          options={[
            { value: "", label: "Все статусы" },
            ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
          ]}
        />
        <Button type="submit" disabled={loading}>Применить</Button>
      </form>
    </div>

    {#if loading}
      <LoadingState message="Загрузка серверов…" />
    {:else if visible.length === 0}
      <EmptyState title="Серверы не найдены" message="Проверьте фильтры." />
    {:else}
      <AdminTable label="Таблица серверов. Прокрутите горизонтально для просмотра всех столбцов">
        <thead>
          <tr>
            <th>Сервер</th>
            <th>Владелец</th>
            <th>Статус</th>
            <th>Нода / IP:Порт</th>
            <th>Ресурсы</th>
            <th>Изменён</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each visible as server (server.id)}
            <tr>
              <td>
                <a class="table-link" href={routeHref("admin-server", { id: server.id })}>
                  <strong>{server.name}</strong>
                </a>
                <small>{server.game} · {server.core} {server.version}</small>
              </td>
              <td>
                <a class="table-link" href={routeHref("admin-user", { id: server.ownerId })}>
                  {server.ownerUsername}
                </a>
              </td>
              <td>
                <Badge status={server.status}>{statusLabels[server.status] ?? server.status}</Badge>
                {#if server.lastError}
                  <small class="table-error">{server.lastError}</small>
                {/if}
              </td>
              <td>
                <small>Нода: {server.nodeName ?? "не назначена"}</small>

                {#if server.nodeIp && server.hostPort}
                  <div><small class="mono-code">{server.nodeIp}:{server.hostPort}</small></div>
                {:else if server.hostPort}
                  <div><small class="mono-code">:{server.hostPort}</small></div>
                {/if}
              </td>
              <td>{server.memLimitMb} МБ</td>
              <td>{formatDateTime(server.updatedAt)}</td>
              <td class="table-actions">
                {#if ["stopped", "failed"].includes(server.status)}
                  <Button
                    variant="ghost"
                    size="compact"
                    onclick={() => void action(server, "start")}
                    disabled={busyId !== null}
                  >
                    Старт
                  </Button>
                {/if}
                {#if ["running", "starting"].includes(server.status)}
                  <Button
                    variant="ghost"
                    size="compact"
                    onclick={() => void action(server, "stop")}
                    disabled={busyId !== null}
                  >
                    Стоп
                  </Button>
                {/if}
                {#if ["running", "starting", "stopping", "queued_stop", "queued_start", "failed"].includes(server.status)}
                  <Button
                    variant="danger"
                    size="compact"
                    onclick={() => void action(server, "kill")}
                    disabled={busyId !== null}
                  >
                    Аварийно остановить
                  </Button>
                {/if}

                {#if isAdmin}
                  <Button
                    variant="danger"
                    size="compact"
                    onclick={() => void action(server, "delete")}
                    disabled={busyId !== null}
                  >
                    Удалить
                  </Button>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </AdminTable>
    {/if}
    <div class="pagination">
      <span>Страница {page}</span>
      <Button
        variant="ghost"
        size="compact"
        disabled={page === 1 || loading}
        onclick={() => void load(page - 1)}>Назад</Button
      >
      <Button
        variant="ghost"
        size="compact"
        disabled={!nextCursor || loading}
        onclick={() => void load(page + 1)}>Далее</Button
      >
    </div>
  </Card>
</AdminShell>

<style>
  .mono-code {
    font-family: var(--font-mono, monospace);
    color: var(--text-muted);
    font-size: 0.78rem;
  }
</style>
